import { Category } from '@shared/types/product';
import { buildCategoryCrumbs, BreadcrumbCrumb } from '@/utils/build-category-crumbs';

// EWM-538 — PLP breadcrumb: clean CT category names (no trailing id), built
// from the category hierarchy via the shared buildCategoryCrumbs util (same
// pattern as the PDP, EWM-539). Falls back to a cleaned slug segment only
// when a category has no name configured in CT.
const slugToLabel = (chunk: string): string =>
  chunk
    .replace(/-\d+$/, '') // drop the trailing CT id (e.g. "-422")
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const buildPlpBreadcrumb = ({
  slugSegments,
  category,
  homeLabel,
}: {
  slugSegments: string[];
  category?: Category;
  homeLabel: string;
}): BreadcrumbCrumb[] => {
  const home: BreadcrumbCrumb = { label: homeLabel, href: '/' };
  const segments = slugSegments.filter(Boolean);
  if (segments.length === 0) return [home];

  // No resolved category (rare — slug not in catalog): derive clean labels
  // from the slug so the trail still renders without the id suffix.
  if (!category) {
    return [
      home,
      ...segments.map((chunk, i, arr) => ({ label: slugToLabel(chunk), href: `/${arr.slice(0, i + 1).join('/')}` })),
    ];
  }

  // Build one ancestor input per intermediate slug segment (root-first),
  // pulling the clean CT name positionally from `category.ancestors` and
  // falling back to a cleaned slug segment when a name is missing. Iterating
  // the segments (not `ancestors`) keeps labels and hrefs aligned even if the
  // ancestor chain and the URL path ever disagree in length — every
  // intermediate segment still renders a crumb.
  const categoryAncestors = category.ancestors ?? [];
  const ancestors = segments.slice(0, -1).map((segment, i) => ({
    name: categoryAncestors[i]?.name ?? slugToLabel(segment),
  }));
  const leafName = category.name ?? slugToLabel(segments[segments.length - 1] ?? '');

  return [home, ...buildCategoryCrumbs({ name: leafName, path: `/${segments.join('/')}`, ancestors })];
};
